import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, Sparkles, Bot, Check, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GENERATE_AD_URL, GENERATE_AUTH_TOKEN } from '../config';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';
import GeneratedImagesInline from '../components/GeneratedImagesInline';
import LoadingAnimation from '../components/LoadingAnimation';
import {
  loadLastCrearAnuncioState,
  mergeLastCrearAnuncioState,
  type CrearAnuncioObjetivo,
} from '../utils/crearAnuncioStorage';

export const SeleccionarEstiloAnuncio: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsRaw = params.get('ids');
  const ids = useMemo(
    () => {
      if (!idsRaw) return [] as string[];
      return idsRaw.split(',').map((s) => s.trim()).filter(Boolean).filter((s) => s !== '0');
    },
    [idsRaw]
  );
  // Fallback for 'modo' when query param is missing: use last saved state
  const savedModoRef = useRef(loadLastCrearAnuncioState());
  // Persist initial variant IDs to survive navigation back from EditarImagen (which may clear query params)
  const persistedVariantIdsRef = useRef<string[]>([]);
  useEffect(() => {
    try {
      if (ids.length > 0) {
        persistedVariantIdsRef.current = ids;
        sessionStorage.setItem('atlas_quick_variant_ids', ids.join(','));
      } else if (persistedVariantIdsRef.current.length === 0) {
        const stored = sessionStorage.getItem('atlas_quick_variant_ids');
        if (stored) {
          const arr = stored.split(',').map((s) => s.trim()).filter(Boolean).filter((s) => s !== '0');
          if (arr.length > 0) persistedVariantIdsRef.current = arr;
        }
      }
    } catch (e) {
      // sessionStorage may be unavailable in some contexts; ignore
      console.warn('Variant ID persistence unavailable:', e);
    }
  }, [ids]);

  // Helper to get variant IDs with fallbacks (query -> ref -> sessionStorage)
  const getVariantIds = useCallback((): string[] => {
    if (ids.length > 0) return ids;
    if (persistedVariantIdsRef.current.length > 0) return persistedVariantIdsRef.current;
    try {
      const stored = sessionStorage.getItem('atlas_quick_variant_ids');
      if (stored) {
        const arr = stored.split(',').map((s) => s.trim()).filter(Boolean).filter((s) => s !== '0');
        if (arr.length > 0) {
          persistedVariantIdsRef.current = arr;
          return arr;
        }
      }
    } catch {}
    try {
      const last = loadLastCrearAnuncioState();
      if (last?.selectedVariantIds && last.selectedVariantIds.length > 0) {
        return last.selectedVariantIds;
      }
    } catch {}
    return [];
  }, [ids]);

  const getBillingCycleRange = (paidPlanStartDate: string) => {
    const now = new Date();
    const subscriptionDate = new Date(paidPlanStartDate);
    const anchorDay = subscriptionDate.getDate();

    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();

    const anchorFor = (year: number, month: number) => {
      const dim = daysInMonth(year, month);
      const day = Math.min(anchorDay, dim);
      return new Date(year, month, day, 0, 0, 0, 0);
    };

    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
    }

    if (start.getTime() < subscriptionDate.getTime()) {
      start = new Date(
        subscriptionDate.getFullYear(),
        subscriptionDate.getMonth(),
        subscriptionDate.getDate(),
        0, 0, 0, 0
      );
    }

    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());

    return {
      start: start.toISOString(),
      end: end.toISOString(),
    };
  };

  const checkSubscriptionLimits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes, paid_plan_start_date')
        .eq('id_company', profile.id_company)
        .single();

      if (!companyProfile) throw new Error('No company profile found');

      const remaining = (companyProfile as any).imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining <= 0) {
        const isFree = (companyProfile as any).plan === 'gratis';
        setSubscriptionLimitType(isFree ? 'free-trial-ended' : 'plan-limit-reached');
        setShowSubscriptionLimitModal(true);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error checking subscription limits:', err);
      return true; // No bloquear ante errores
    }
  };

  const modoParam = params.get('modo') as 'rapido' | 'personalizado' | null;
  const modo = useMemo(() => {
    if (modoParam === 'rapido' || modoParam === 'personalizado') return modoParam;
    const saved = savedModoRef.current?.modo as 'rapido' | 'personalizado' | undefined;
    return saved ?? 'rapido';
  }, [modoParam]);
  const objetivo = params.get('objetivo') || '';
  const detalles_oferta = params.get('detalles_oferta') || '';
  const tipo = (params.get('tipo') as 'imagen' | 'video') || 'imagen';
  const formato = (params.get('formato') as 'post' | 'story' | 'square') || 'post';
  const cantidadImagenesRaw = params.get('cantidad_imagenes');
  const cantidadImagenes = useMemo(
    () => {
      const n = Number(cantidadImagenesRaw);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    },
    [cantidadImagenesRaw]
  );
  const modoPro = params.get('modo_pro') === '1';

  type EstiloKey = 'minimalista' | 'creativo' | 'automatico';

  const savedStateRef = useRef(loadLastCrearAnuncioState());
  const estiloParam = params.get('estilo') as EstiloKey | null;
  const allowedEstilos: EstiloKey[] = ['minimalista', 'creativo', 'automatico'];
  const [estilo, setEstilo] = useState<EstiloKey>(() => {
    if (estiloParam && allowedEstilos.includes(estiloParam)) return estiloParam;
    const saved = savedStateRef.current?.estilo as EstiloKey | undefined;
    return saved && allowedEstilos.includes(saved) ? saved : 'automatico';
  });
  const [generating, setGenerating] = useState(false);
  const [completeProgressNow, setCompleteProgressNow] = useState<boolean>(false);
  const [error, setError] = useState<React.ReactNode | null>(null);
  const consecutiveGenErrorsRef = useRef<number>(0);
  const willShowResultsRef = useRef<boolean>(false);
  const [showResults, setShowResults] = useState(false);
  const [generatedImageIds, setGeneratedImageIds] = useState<number[]>([]);
  const [companyIdForImages, setCompanyIdForImages] = useState<string>('');
  // Prevent click-through when closing results
  const [clickShield, setClickShield] = useState<boolean>(false);
  // Subscription/quota blocking state
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] = useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<'free-trial-ended' | 'plan-limit-reached'>('free-trial-ended');

  // Progress bar duration: 70s for quick mode, 35s for personalized mode
  const progressDurationMs = useMemo(() => (modo === 'rapido' ? 80000 : 35000), [modo]);

  // Scroll to top on mount (iOS Safari fix)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Rehydrate results when returning from EditarImagen via navigation state
  useEffect(() => {
    const st = (location.state as any) || {};
    if (st.returnToResults && Array.isArray(st.resultsImageIds)) {
      setGeneratedImageIds(st.resultsImageIds as number[]);
      setCompanyIdForImages((st.resultsCompanyId as string) || '');
      setShowResults(true);
      // Clear state to avoid re-triggering on further renders
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state]);

  const estilos: Array<{
    key: EstiloKey;
    title: string;
    description: string;
    Icon: React.ComponentType<any>;
    colorBg: string;
    colorText: string;
  }> = [
    {
      key: 'minimalista',
      title: 'Minimalista',
      description: 'Imágenes estilo catálogo. IA restringida a fondo blanco, gris o degradado sutiles.',
      Icon: LayoutGrid,
      colorBg: 'bg-blue-50',
      colorText: 'text-blue-600',
    },
    {
      key: 'creativo',
      title: 'Creativo / Inspiracional',
      description: 'IA buscará generar escenas y ambientes originales, adaptados al estilo de tu marca.',
      Icon: Sparkles,
      colorBg: 'bg-purple-50',
      colorText: 'text-purple-600',
    },
    {
      key: 'automatico',
      title: 'Automático',
      description: 'Dejá que la IA decida que estilo usar.',
      Icon: Bot,
      colorBg: 'bg-gray-50',
      colorText: 'text-gray-700',
    },
  ];

  useEffect(() => {
    const trimmedDetalles = detalles_oferta.trim();
    const allowedObjetivos: CrearAnuncioObjetivo[] = ['mostrar', 'oferta', 'lanzamiento', 'evento'];
    const objetivoValue = allowedObjetivos.includes(objetivo as CrearAnuncioObjetivo)
      ? (objetivo as CrearAnuncioObjetivo)
      : undefined;
    const variantIdsToPersist = getVariantIds();

    mergeLastCrearAnuncioState({
      modo,
      selectedVariantIds: variantIdsToPersist.length > 0 ? variantIdsToPersist : undefined,
      objetivo: objetivoValue ?? null,
      detallesOferta: trimmedDetalles ? trimmedDetalles : null,
      tipo,
      formato,
      cantidadImagenes: cantidadImagenes ?? undefined,
      estilo,
    });
  }, [getVariantIds, modo, objetivo, detalles_oferta, tipo, formato, cantidadImagenes, estilo]);

  const handleContinue = () => {
    if (!estilo) return;
    const qp = new URLSearchParams();
    qp.set('ids', ids.join(','));
    qp.set('modo', modo);
    if (objetivo) qp.set('objetivo', objetivo);
    if (detalles_oferta.trim()) qp.set('detalles_oferta', detalles_oferta.trim());
    qp.set('tipo', tipo);
    qp.set('formato', formato);
    qp.set('estilo', estilo);
    navigate(`/redes-sociales/crear-anuncio/configurar?${qp.toString()}`);
  };

  // Preload images by fetching signed URLs and loading them into browser cache
  type PreloadedImage = { id: number; fileName: string; signedUrl: string; favorito: boolean };
  const preloadGeneratedImages = async (imageIds: number[], companyId: string): Promise<PreloadedImage[]> => {
    const preloaded: PreloadedImage[] = [];
    const maxAttempts = 10;
    const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

    for (const imageId of imageIds) {
      let loaded = false;
      for (let attempt = 1; attempt <= maxAttempts && !loaded; attempt++) {
        try {
          const { data: imageData } = await supabase
            .from('imagenes_generadas')
            .select('file_name, favorito')
            .eq('id', imageId)
            .eq('id_company', companyId)
            .single();

          if (!imageData?.file_name) {
            await delay(1000);
            continue;
          }

          const filePath = `${companyId}/full/${imageData.file_name}`;
          const { data: signedUrlData } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
          
          if (!signedUrlData?.signedUrl) {
            await delay(1000);
            continue;
          }

          // Preload image into browser cache
          await new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = signedUrlData.signedUrl;
          });

          preloaded.push({
            id: imageId,
            fileName: imageData.file_name,
            signedUrl: signedUrlData.signedUrl,
            favorito: imageData.favorito === true,
          });
          loaded = true;
        } catch (err) {
          console.warn(`Preload attempt ${attempt} failed for image ${imageId}:`, err);
          await delay(1000);
        }
      }
    }
    return preloaded;
  };

  const handleGenerateMore = async () => {
    if (generating) return;
    try {
      setError(null);
      // Keep main overlay visible while fetching and loading new images
      // Start overlay immediately, then check subscription limits
      setGenerating(true);
      setCompleteProgressNow(false);
      const canGenerate = await checkSubscriptionLimits();
      if (!canGenerate) {
        setGenerating(false);
        return;
      }

      // 1) Get user and company
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No se encontró la empresa del usuario');

      // 2) Build format description
      let formatName = '';
      switch (formato) {
        case 'story':
          formatName = '9:16';
          break;
        case 'square':
          formatName = '1:1';
          break;
        case 'post':
        default:
          formatName = '4:5';
      }

      //codigo legacy de format description
      /*let formatName = '';
      switch (formato) {
        case 'story':
          formatName = 'Imagen vertical con relación de aspecto 9:16 (nueve dieciseisavos). La imagen será alta y estrecha, con la altura mucho mayor que la anchura. Se pide un formato vertical.';
          break;
        case 'square':
          formatName = 'Imagen cuadrada con relación de aspecto 1:1 (uno a uno). La imagen tendrá la misma altura y anchura. Se pide un formato cuadrado.';
          break;
        case 'post':
        default:
          formatName = 'Imagen vertical con relación de aspecto 4:5 (cuatro quintos). La imagen tendrá una altura ligeramente mayor que su anchura. Se pide un formato vertical.';
      }*/

      // Background prompt/focus removed — no longer sent in payload

      // 4) Determine ids to use (fallback to persisted/session/state when no query)
      const variantIds = getVariantIds();
      if (variantIds.length === 0) {
        try { console.warn('[SeleccionarEstiloAnuncio] handleGenerateMore called without variantIds; aborting'); } catch {}
        setGenerating(false);
        setCompleteProgressNow(true);
        setError('No se encontraron productos seleccionados para generar más imágenes. Volvé a seleccionar productos y generá de nuevo.');
        return;
      }
      const mode = variantIds.length > 1 ? 'multiple' : 'single';

      // 5) Build request payload
      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode, // single | multiple (según cantidad de variantes)
        modo, // 'rapido' | 'personalizado'
        estilo, // 'minimalista' | 'creativo' | 'automatico'
        tipo, // 'imagen' | 'video'
        cantidad_imagenes: tipo === 'imagen' ? 2 : undefined,
        objetivo: objetivo || undefined,
        detalles_oferta: detalles_oferta.trim() ? detalles_oferta.trim() : undefined,
        Format: formatName,
        modo_pro: modoPro
      };

      // 5b) Reuse 'composicion' and 'pose' (and background) from last saved state if present
      const saved = loadLastCrearAnuncioState();
      {
        const FASHION_LABEL = 'Moda e Indumentaria';
        const isFashion = ((saved?.rubro as string | undefined) ?? FASHION_LABEL) === FASHION_LABEL;
        if (modo === 'personalizado' && !isFashion) {
          requestData.composicion = 'producto';
        } else if (saved?.composicion) {
          requestData.composicion = saved.composicion;
        }
        if (!(modo === 'personalizado' && !isFashion) && saved?.pose) {
          requestData.pose = saved.pose;
        }
      }
      if (saved?.background?.mode) {
        const bg = saved.background;
        requestData.background_mode = bg.mode;
        if (bg.mode === 'elegido') {
          if (typeof bg.id === 'number' && Number.isFinite(bg.id)) requestData.background_id = bg.id;
          if (!requestData.background_id && bg.url) requestData.background_url = bg.url;
        } else if (bg.mode === 'blanco' && modo === 'personalizado') {
          requestData.background_id = 5;
        }
        if (bg.mode === 'descripcion' && bg.text && bg.text.trim()) {
          requestData.background_text = bg.text.trim();
        }
      }

      // 6) Call webhook
      try { console.log('[SeleccionarEstiloAnuncio] handleGenerateMore payload', requestData); } catch {}
      const response = await fetch(GENERATE_AD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData)
      });

      let __respTextGM: string | null = null;
      try { __respTextGM = await response.clone().text(); } catch {}
      try { console.log('[SeleccionarEstiloAnuncio] handleGenerateMore response', { status: response.status, ok: response.ok, text: __respTextGM }); } catch {}
      if (!response.ok) {
        console.error('[SeleccionarEstiloAnuncio] handleGenerateMore not ok');
        throw new Error('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      }

      let data: any = null;
      try { data = await response.json(); } catch (e) { console.error('[SeleccionarEstiloAnuncio] handleGenerateMore json parse error', e); }
      try { console.log('[SeleccionarEstiloAnuncio] handleGenerateMore json', data); } catch {}
      if (data?.response === 'success' && Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        willShowResultsRef.current = true;
        const newImageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult = (data.id_company as string) || profile.id_company;
        
        // Preload only the NEW images before navigating
        let preloadedImages: PreloadedImage[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(newImageIds, companyIdResult);
        } catch (preloadErr) {
          console.warn('[SeleccionarEstiloAnuncio] handleGenerateMore preload failed:', preloadErr);
        }
        
        setGeneratedImageIds((prev) => [...prev, ...newImageIds]);
        setCompanyIdForImages(companyIdResult);
        setCompleteProgressNow(true);
        try { window.sessionStorage.setItem('atlas:crear-anuncio:has-generated', '1'); } catch {}
        // Navigate to dedicated generated images page with preloaded images
        navigate('/redes-sociales/imagenes-generadas', {
          state: { imageIds: [...generatedImageIds, ...newImageIds], companyId: companyIdResult, preloadedImages }
        });
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (e) {
      console.error(e);
      const next = (consecutiveGenErrorsRef.current || 0) + 1;
      consecutiveGenErrorsRef.current = next;
      if (next >= 2) {
        setError(
          <span>
            Se produjo un error. No se consumieron imágenes. Es posible que los servidores estén momentaneamente en mantenimento. Si prefieres puedes contactarte con el{' '}
            <button
              type="button"
              onClick={() => navigate('/ayuda')}
              className="font-semibold underline"
            >
              servicio de ayuda
            </button>
            .
          </span>
        );
      } else {
        setError('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      }
      setCompleteProgressNow(true);
    }
  };

  const handleGenerate = async () => {
    // If it's the personalized flow, keep current navigation
    if (modo === 'personalizado') {
      handleContinue();
      return;
    }

    if (!estilo || ids.length === 0 || generating) return;
    try {
      // Check subscription limits before generating
      const canGenerate = await checkSubscriptionLimits();
      if (!canGenerate) {
        return;
      }
      setGenerating(true);
      setCompleteProgressNow(false);
      setError(null);

      // 1) Get user and company
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No se encontró la empresa del usuario');

      // 2) Build format description
      let formatName = '';
      switch (formato) {
        case 'story':
          formatName = '9:16';
          break;
        case 'square':
          formatName = '1:1';
          break;
        case 'post':
        default:
          formatName = '4:5';
      }

      // Background prompt/focus removed — no longer sent in payload

      // 4) Determine ids to use (fallback to persisted when no query)
      const variantIds = getVariantIds();
      const mode = variantIds.length > 1 ? 'multiple' : 'single';

      // 5) Build request payload
      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode, // single | multiple (según cantidad de variantes)
        modo, // 'rapido' | 'personalizado'
        estilo, // 'minimalista' | 'creativo' | 'automatico'
        tipo, // 'imagen' | 'video'
        cantidad_imagenes: tipo === 'imagen' ? cantidadImagenes : undefined,
        objetivo: objetivo || undefined,
        detalles_oferta: detalles_oferta.trim() ? detalles_oferta.trim() : undefined,
        Format: formatName,
        modo_pro: modoPro
      };

      // Reuse 'composicion' and 'pose' (and background) from last saved state if present
      {
        const saved = loadLastCrearAnuncioState();
        const FASHION_LABEL = 'Moda e Indumentaria';
        const isFashion = ((saved?.rubro as string | undefined) ?? FASHION_LABEL) === FASHION_LABEL;
        if (modo === 'personalizado' && !isFashion) {
          requestData.composicion = 'producto';
        } else if (saved?.composicion) {
          requestData.composicion = saved.composicion;
        }
        if (!(modo === 'personalizado' && !isFashion) && saved?.pose) {
          requestData.pose = saved.pose;
        }
        if (saved?.background?.mode) {
          const bg = saved.background;
          requestData.background_mode = bg.mode;
          if (bg.mode === 'elegido') {
            if (typeof bg.id === 'number' && Number.isFinite(bg.id)) requestData.background_id = bg.id;
            if (!requestData.background_id && bg.url) requestData.background_url = bg.url;
          } else if (bg.mode === 'blanco' && modo === 'personalizado') {
            requestData.background_id = 5;
          }
          if (bg.mode === 'descripcion' && bg.text && bg.text.trim()) {
            requestData.background_text = bg.text.trim();
          }
        }
      }

      // 6) Call webhook
      try { console.log('[SeleccionarEstiloAnuncio] handleGenerate payload', requestData); } catch {}
      const response = await fetch(GENERATE_AD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData)
      });
      let __respTextG: string | null = null;
      try { __respTextG = await response.clone().text(); } catch {}
      try { console.log('[SeleccionarEstiloAnuncio] handleGenerate response', { status: response.status, ok: response.ok, text: __respTextG }); } catch {}
      if (!response.ok) {
        console.error('[SeleccionarEstiloAnuncio] handleGenerate not ok');
        throw new Error('Error al generar imágenes');
      }

      let data: any = null;
      try { data = await response.json(); } catch (e) { console.error('[SeleccionarEstiloAnuncio] handleGenerate json parse error', e); }
      try { console.log('[SeleccionarEstiloAnuncio] handleGenerate json', data); } catch {}
      if (data?.response === 'success' && Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        willShowResultsRef.current = true;
        const imageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult = (data.id_company as string) || profile.id_company;
        setGeneratedImageIds(imageIds);
        setCompanyIdForImages(companyIdResult);
        setCompleteProgressNow(true);
        consecutiveGenErrorsRef.current = 0;
        setError(null);
        try { window.sessionStorage.setItem('atlas:crear-anuncio:has-generated', '1'); } catch {}
        // Navigate to dedicated generated images page
        navigate('/redes-sociales/imagenes-generadas', {
          state: { imageIds, companyId: companyIdResult }
        });
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (e) {
      console.error(e);
      const next = (consecutiveGenErrorsRef.current || 0) + 1;
      consecutiveGenErrorsRef.current = next;
      if (next >= 2) {
        setError(
          <span>
            Se produjo un error. No se consumieron imágenes. Es posible que los servidores estén momentaneamente en mantenimento. Si prefieres puedes contactarte con el{' '}
            <button
              type="button"
              onClick={() => navigate('/ayuda')}
              className="font-semibold underline"
            >
              servicio de ayuda
            </button>
            .
          </span>
        );
      } else {
        setError('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      }
      setCompleteProgressNow(true);
    }
  };

  return (
    <div className="p-4 sm:p-8 sm:pb-0 max-w-4xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent" style={{ pointerEvents: clickShield ? 'none' : 'auto' }}>
      {!showResults ? (
        <>
          {/* Header + progress */}
          <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6">
            {/* Mobile header */}
            <div className="sm:hidden mt-2 mb-4 text-center">
              <h1 className="text-sm font-semibold text-gray-900">Seleccioná el estilo<br className="sm:hidden" /> del anuncio</h1>
              <p className="text-sm text-gray-600 mt-3">Modo {modo === 'personalizado' ? 'personalizado' : 'automático'} · {ids.length} productos seleccionados</p>
            </div>

            {/* Desktop header */}
            <div className="hidden sm:flex items-start justify-between mb-6">
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Seleccioná el estilo del anuncio</h1>
                <p className="text-sm text-gray-600 mt-1">Modo {modo === 'personalizado' ? 'personalizado' : 'automático'} · {ids.length} productos seleccionados</p>
              </div>
            </div>
            {error && (
              <div className="mb-3 bg-red-50 text-red-700 px-4 py-3 text-sm">
                {error}
              </div>
            )}
            <div className="mt-4">
              <div className="w-full h-1 bg-gray-200 rounded-full">
                <div className="h-1 bg-blue-500 rounded-full" style={{ width: '90%' }} />
              </div>
            </div>

            {/* Opciones de estilo */}
            <div className="mt-6 space-y-3">
              {estilos.map(({ key, title, description, Icon, colorBg, colorText }) => {
                const selected = estilo === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setEstilo(key)}
                    className={`w-full text-left border ${selected ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-200'} rounded-2xl p-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorBg}`}>
                        <Icon className={`w-5 h-5 ${colorText}`} />
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-900 font-semibold text-sm">{title}</p>
                        <p className="text-gray-600 text-sm mt-1">{description}</p>
                      </div>
                      {selected && (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                          <Check className="w-4 h-4" />
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer actions (desktop) */}
            <div className="mt-6 hidden sm:flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Volver
              </button>

              <button
                type="button"
                disabled={!estilo || generating}
                className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${(!estilo || generating) ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                onClick={handleGenerate}
              >
                {generating ? (
                  <>
                    <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Sparkles aria-hidden="true" className="w-4 h-4" />
                    Generar
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Mobile spacer for fixed bottom bar */}
          <div className="sm:hidden" style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 64px)' }} />

          {/* Mobile bottom bar */}
          <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
            <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Volver
              </button>
              <button
                type="button"
                disabled={!estilo || generating}
                className={`inline-flex items-center gap-2 px-4 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${(!estilo || generating) ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                onClick={handleGenerate}
              >
                {generating ? (
                  <>
                    <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Sparkles aria-hidden="true" className="w-4 h-4" />
                    Generar
                  </>
                )}
              </button>
            </div>
          </div>
          {false && error && (
            <div className="sm:max-w-4xl sm:mx-auto mt-3 text-sm text-red-600">{error}</div>
          )}
        </>
      ) : (
        <div className="mt-2">
          <GeneratedImagesInline
            imageIds={generatedImageIds}
            companyId={companyIdForImages}
            onClose={() => {
              // Shield + delayed hide to avoid click-through
              setClickShield(true);
              setTimeout(() => setShowResults(false), 50);
              setTimeout(() => setClickShield(false), 300);
            }}
            onReturnToConfig={() => {
              try { console.log('[SeleccionarEstiloAnuncio] onReturnToConfig called', { modo, pathname: window.location.pathname, search: window.location.search }); } catch {}
              // Shield + delayed hide to avoid click-through
              setClickShield(true);
              setTimeout(() => setShowResults(false), 50);
              setTimeout(() => setClickShield(false), 300);
              try { console.log('[SeleccionarEstiloAnuncio] navigating to seleccionar-productos after closing results'); } catch {}
              const persisted = loadLastCrearAnuncioState();
              const resolvedIds = persisted?.selectedVariantIds && persisted.selectedVariantIds.length > 0
                ? persisted.selectedVariantIds
                : getVariantIds();
              const nextModo = persisted?.modo || modo;
              const qp = new URLSearchParams();
              if (resolvedIds.length > 0) qp.set('ids', resolvedIds.join(','));
              if (nextModo) qp.set('modo', nextModo);
              if (persisted?.objetivo) qp.set('objetivo', persisted.objetivo);
              if (persisted?.detallesOferta) qp.set('detalles_oferta', persisted.detallesOferta);
              const query = qp.toString();
              navigate(query ? `/redes-sociales/crear-anuncio/seleccionar-productos?${query}` : '/redes-sociales/crear-anuncio/seleccionar-productos');
            }}
            onGenerateMore={handleGenerateMore}
            onReady={() => { willShowResultsRef.current = false; setGenerating(false); }}
          />
        </div>
      )}
      {generating && (
        <LoadingAnimation
          message="Generando imágenes..."
          subMessage="Esto puede tardar unos segundos"
          durationMs={progressDurationMs}
          completeNow={completeProgressNow}
          onCompleted={() => { if (!willShowResultsRef.current) setGenerating(false); }}
        />
      )}
      {/* Subscription limit modal */}
      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        onClose={() => setShowSubscriptionLimitModal(false)}
        type={subscriptionLimitType}
      />
    </div>
  );
};

export default SeleccionarEstiloAnuncio;
